// Alert payload viewer: markdown text, collapsible JSON, and a read-only
// browser for MinIO folders linked from the payload (via erecords/files.go).
import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import {
  BrokenImage,
  ChevronLeft,
  ChevronRight,
  Close,
  ContentCopy,
  Download,
  ExpandMore,
  InsertDriveFile,
  Folder,
  Image as ImageIcon,
} from '@mui/icons-material'
import Markdown from '../util/Markdown'
import { pathPrefix } from '../env'
import { Panel, useBrand } from './ui'

const mono = "ui-monospace, 'Cascadia Code', 'SF Mono', Consolas, monospace"

type Part =
  | { kind: 'md'; text: string }
  | { kind: 'json'; value: unknown }
  | { kind: 'code'; text: string }

function tryJSON(s: string): { ok: boolean; value?: unknown } {
  const t = s.trim()
  if (!/^[[{]/.test(t)) return { ok: false }
  try {
    return { ok: true, value: JSON.parse(t) }
  } catch {
    return { ok: false }
  }
}

// parseDetails splits alert details into markdown, JSON and code parts.
export function parseDetails(details: string): Part[] {
  const whole = tryJSON(details)
  if (whole.ok) return [{ kind: 'json', value: whole.value }]
  const parts: Part[] = []
  const re = /```(\w*)\s*\n([\s\S]*?)```/g
  let last = 0
  for (let m = re.exec(details); m; m = re.exec(details)) {
    if (m.index > last)
      parts.push({ kind: 'md', text: details.slice(last, m.index) })
    const j = tryJSON(m[2])
    parts.push(
      j.ok ? { kind: 'json', value: j.value } : { kind: 'code', text: m[2] },
    )
    last = re.lastIndex
  }
  if (last < details.length)
    parts.push({ kind: 'md', text: details.slice(last) })
  // drop a "Payload" heading line (the card is already titled so) and empty text
  return parts
    .map((p) =>
      p.kind === 'md'
        ? { ...p, text: p.text.replace(/^#+\s*payload\s*$/gim, '') }
        : p,
    )
    .filter((p) => p.kind !== 'md' || p.text.trim())
}

export function linksIn(details: string): string[] {
  const urls = details.match(/https?:\/\/[^\s)\]"'<>`]+/g) ?? []
  return Array.from(new Set(urls.map((u) => u.replace(/[.,;:]+$/, ''))))
}

// ---------------------------------------------------------------- JSON

function JsonNode(props: {
  name?: string
  value: unknown
  depth: number
  open: number // expand nodes shallower than this
}): React.JSX.Element {
  const c = useBrand()
  const isObj = props.value !== null && typeof props.value === 'object'
  const [expanded, setExpanded] = useState(props.depth < props.open)
  useEffect(
    () => setExpanded(props.depth < props.open),
    [props.open, props.depth],
  )

  const key = props.name !== undefined && (
    <span style={{ color: c.muted }}>{props.name}: </span>
  )
  if (!isObj) {
    const v = props.value
    const color =
      typeof v === 'string' ? c.ok : typeof v === 'number' ? '#3b6ea8' : c.warn
    return (
      <div
        style={{
          paddingLeft: 18,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {key}
        <span style={{ color: v === null ? c.muted : color }}>
          {JSON.stringify(v)}
        </span>
      </div>
    )
  }
  const entries = Array.isArray(props.value)
    ? props.value.map((v, i) => [String(i), v] as const)
    : Object.entries(props.value as Record<string, unknown>)
  const [openB, closeB] = Array.isArray(props.value) ? ['[', ']'] : ['{', '}']
  return (
    <div>
      <div
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded(!expanded)
          }
        }}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        {expanded ? (
          <ExpandMore sx={{ fontSize: 16, color: c.muted }} />
        ) : (
          <ChevronRight sx={{ fontSize: 16, color: c.muted }} />
        )}
        {key}
        <span style={{ color: c.muted }}>
          {openB}
          {!expanded &&
            ` ${entries.length} ${Array.isArray(props.value) ? 'items' : 'keys'} ${closeB}`}
        </span>
      </div>
      {expanded && (
        <div
          style={{
            paddingLeft: 14,
            borderLeft: `1px solid ${c.line}`,
            marginLeft: 7,
          }}
        >
          {entries.map(([k, v]) => (
            <JsonNode
              key={k}
              name={Array.isArray(props.value) ? undefined : k}
              value={v}
              depth={props.depth + 1}
              open={props.open}
            />
          ))}
        </div>
      )}
      {expanded && (
        <div style={{ color: c.muted, paddingLeft: 18 }}>{closeB}</div>
      )}
    </div>
  )
}

export function JsonView({ value }: { value: unknown }): React.JSX.Element {
  const c = useBrand()
  const [open, setOpen] = useState(3)
  const [copied, setCopied] = useState(false)
  return (
    <Box
      sx={{
        border: `1px solid ${c.line}`,
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction='row'
        spacing={1}
        sx={{
          px: 1.5,
          py: 0.75,
          bgcolor: c.bg,
          borderBottom: `1px solid ${c.line}`,
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: c.muted, flex: 1 }}
        >
          JSON
        </Typography>
        <Button size='small' onClick={() => setOpen(99)}>
          Expand all
        </Button>
        <Button size='small' onClick={() => setOpen(1)}>
          Collapse
        </Button>
        <Button
          size='small'
          startIcon={<ContentCopy fontSize='small' />}
          onClick={() =>
            navigator.clipboard
              .writeText(JSON.stringify(value, null, 2))
              .then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              })
          }
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Stack>
      <Box
        sx={{
          p: 1.5,
          fontFamily: mono,
          fontSize: 12.5,
          lineHeight: 1.6,
          overflowX: 'auto',
          maxHeight: 520,
        }}
      >
        <JsonNode value={value} depth={0} open={open} />
      </Box>
    </Box>
  )
}

export function PayloadView({
  details,
}: {
  details: string
}): React.JSX.Element {
  const c = useBrand()
  const parts = useMemo(() => parseDetails(details), [details])
  if (!details.trim()) {
    return (
      <Typography sx={{ color: c.muted }}>
        No details were sent with this alert.
      </Typography>
    )
  }
  return (
    <Stack spacing={1.5}>
      {parts.map((p, i) => {
        switch (p.kind) {
          case 'json':
            return <JsonView key={i} value={p.value} />
          case 'code':
            return (
              <Box
                key={i}
                component='pre'
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: c.bg,
                  fontFamily: mono,
                  fontSize: 12.5,
                  overflowX: 'auto',
                }}
              >
                {p.text}
              </Box>
            )
          default:
            return (
              <Box
                key={i}
                sx={{
                  fontSize: 14,
                  '& p': { my: 0.5 },
                  '& a': { color: c.ink },
                }}
              >
                <Markdown value={p.text} />
              </Box>
            )
        }
      })}
    </Stack>
  )
}

// ---------------------------------------------------------------- files

type Entry = {
  name: string
  key: string
  folder: boolean
  size: number
  modified?: string
}
type Listing = {
  bucket: string
  prefix: string
  entries: Entry[]
  more: boolean
}

const fileURL = (bucket: string, key: string, download = false): string =>
  `${pathPrefix}/api/v2/erecords/file?bucket=${encodeURIComponent(bucket)}&object=${encodeURIComponent(key)}${download ? '&download=1' : ''}`

const isImage = (n: string): boolean => /\.(png|jpe?g|gif|webp|bmp)$/i.test(n)

// Thumb loads a picture through the file proxy: a shimmer while it loads, a
// fade-in when ready, two quiet retries, and then a "Couldn't load" tile with
// Retry, never the browser's broken-image icon with the file name.
function Thumb(props: {
  src: string
  height: number | string
}): React.JSX.Element {
  const c = useBrand()
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading')
  const [attempt, setAttempt] = useState(0)
  const src = attempt
    ? `${props.src}${props.src.includes('?') ? '&' : '?'}retry=${attempt}`
    : props.src
  const retry = (): void => {
    setState('loading')
    setAttempt((a) => a + 1)
  }
  return (
    <Box
      sx={{
        position: 'relative',
        height: props.height,
        bgcolor: c.bg,
        overflow: 'hidden',
      }}
    >
      {state === 'loading' && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(90deg, ${c.bg} 0%, ${c.hover} 50%, ${c.bg} 100%)`,
            backgroundSize: '200% 100%',
            animation: 'erecordsShimmer 1.4s ease-in-out infinite',
            '@keyframes erecordsShimmer': {
              '0%': { backgroundPosition: '200% 0' },
              '100%': { backgroundPosition: '-200% 0' },
            },
          }}
        />
      )}
      {state === 'error' ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            color: c.muted,
            fontSize: 12,
          }}
        >
          <BrokenImage sx={{ fontSize: 22 }} />
          Couldn&apos;t load
          <Button
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              retry()
            }}
            sx={{ minHeight: 0, py: 0 }}
          >
            Retry
          </Button>
        </Box>
      ) : (
        <Box
          component='img'
          key={src}
          src={src}
          alt=''
          loading='lazy'
          decoding='async'
          onLoad={() => setState('ok')}
          onError={() => {
            if (attempt < 2) setTimeout(retry, 800 * (attempt + 1))
            else setState('error')
          }}
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: state === 'ok' ? 1 : 0,
            transition: 'opacity 200ms',
          }}
        />
      )}
    </Box>
  )
}

const isVideo = (n: string): boolean => /\.(webm|mp4|mov)$/i.test(n)
const isText = (n: string): boolean =>
  /\.(json|txt|log|md|csv|xml|ya?ml|har)$/i.test(n)

function size(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

// Preview is a gallery over the folder's files: arrows (and the ← → keys)
// move between them, a strip of thumbnails jumps anywhere, Esc closes.
function Preview(props: {
  bucket: string
  files: Entry[]
  index: number | null
  onIndex: (i: number | null) => void
}): React.JSX.Element {
  const c = useBrand()
  const n = props.files.length
  const i = props.index
  const e = i === null ? undefined : props.files[i]
  const go = (d: number): void => {
    if (i !== null && n) props.onIndex((i + d + n) % n)
  }
  const [text, setText] = useState<string>()
  useEffect(() => {
    setText(undefined)
    if (!e || !isText(e.name)) return
    fetch(fileURL(props.bucket, e.key), { credentials: 'same-origin' })
      .then((r) => r.text())
      .then(setText)
      .catch((err) => setText(String(err)))
  }, [e?.key])
  useEffect(() => {
    if (i === null) return
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'ArrowRight') go(1)
      if (ev.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [i, n])
  const json = text !== undefined ? tryJSON(text) : { ok: false }
  const arrow = (side: 'left' | 'right'): React.JSX.Element => (
    <IconButton
      aria-label={side === 'left' ? 'Previous file' : 'Next file'}
      onClick={() => go(side === 'left' ? -1 : 1)}
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [side]: 8,
        bgcolor: 'rgba(26,24,25,0.55)',
        color: '#fff',
        '&:hover': { bgcolor: 'rgba(26,24,25,0.8)' },
      }}
    >
      {side === 'left' ? <ChevronLeft /> : <ChevronRight />}
    </IconButton>
  )
  return (
    <Dialog
      open={Boolean(e)}
      onClose={() => props.onIndex(null)}
      maxWidth='xl'
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: c.muted, flexShrink: 0 }}>
          {i !== null && `${i + 1} / ${n}`}
        </Typography>
        <Typography sx={{ flex: 1, fontWeight: 600 }} noWrap>
          {e?.name}
        </Typography>
        {e && (
          <Button
            size='small'
            startIcon={<Download />}
            href={fileURL(props.bucket, e.key, true)}
          >
            Download
          </Button>
        )}
        <IconButton onClick={() => props.onIndex(null)} aria-label='Close'>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            minHeight: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor:
              e && (isImage(e.name) || isVideo(e.name)) ? c.bg : undefined,
            borderRadius: '12px',
          }}
        >
          {e && isImage(e.name) && (
            <Box
              component='img'
              src={fileURL(props.bucket, e.key)}
              alt={e.name}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
          {e && isVideo(e.name) && (
            <Box
              component='video'
              key={e.key}
              src={fileURL(props.bucket, e.key)}
              controls
              sx={{ maxWidth: '100%', maxHeight: '70vh' }}
            />
          )}
          {e && isText(e.name) && (
            <Box sx={{ width: '100%' }}>
              {json.ok ? (
                <JsonView value={(json as { value: unknown }).value} />
              ) : (
                <Box
                  component='pre'
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: c.bg,
                    borderRadius: '10px',
                    fontFamily: mono,
                    fontSize: 12.5,
                    whiteSpace: 'pre-wrap',
                    maxHeight: '70vh',
                    overflow: 'auto',
                  }}
                >
                  {text ?? 'Loading…'}
                </Box>
              )}
            </Box>
          )}
          {e && !isImage(e.name) && !isVideo(e.name) && !isText(e.name) && (
            <Typography sx={{ color: c.muted }}>
              No preview for this file type; use Download.
            </Typography>
          )}
          {n > 1 && arrow('left')}
          {n > 1 && arrow('right')}
        </Box>
        {n > 1 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              pt: 1.5,
              pb: 0.5,
            }}
          >
            {props.files.map((f, k) => (
              <Box
                key={f.key}
                onClick={() => props.onIndex(k)}
                title={f.name}
                sx={{
                  flexShrink: 0,
                  width: 84,
                  height: 56,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: c.bg,
                  border: `2px solid ${k === i ? c.ink : 'transparent'}`,
                  opacity: k === i ? 1 : 0.65,
                  '&:hover': { opacity: 1 },
                }}
              >
                {isImage(f.name) ? (
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <Thumb src={fileURL(props.bucket, f.key)} height='100%' />
                  </Box>
                ) : (
                  <InsertDriveFile sx={{ color: c.muted }} />
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

// FileBrowser shows a MinIO folder from a link; renders nothing for links
// that are not MinIO (or when file browsing is not configured).
export function FileBrowser({
  link,
}: {
  link: string
}): React.JSX.Element | null {
  const c = useBrand()
  const [listing, setListing] = useState<Listing>()
  const [root, setRoot] = useState<{ bucket: string; prefix: string }>()
  const [err, setErr] = useState('')
  const [hidden, setHidden] = useState(false)
  const [preview, setPreview] = useState<number | null>(null)

  const load = (q: string): void => {
    fetch(`${pathPrefix}/api/v2/erecords/files?${q}`, {
      credentials: 'same-origin',
    })
      .then(async (r) => {
        if (r.status === 400 || r.status === 501) {
          setHidden(true)
          return
        }
        if (!r.ok) throw new Error((await r.text()).trim())
        const l: Listing = await r.json()
        setListing(l)
        setErr('')
        setRoot((old) => old ?? { bucket: l.bucket, prefix: l.prefix })
      })
      .catch((e: Error) => setErr(e.message))
  }
  useEffect(() => load('url=' + encodeURIComponent(link)), [link])
  if (hidden) return null

  const open = (prefix: string): void =>
    load(
      `bucket=${encodeURIComponent(listing?.bucket ?? '')}&prefix=${encodeURIComponent(prefix)}`,
    )
  const files = listing?.entries.filter((e) => !e.folder) ?? []
  const images = files.filter((e) => isImage(e.name))
  const show = (e: Entry): void => setPreview(files.indexOf(e))
  const crumbs = (listing?.prefix ?? '').split('/').filter(Boolean)

  return (
    <Panel
      flush
      title='Files'
      subtitle={
        <Breadcrumbs sx={{ fontSize: 13 }} separator='/'>
          <Link
            component='button'
            onClick={() => open('')}
            underline='hover'
            sx={{ color: c.muted }}
          >
            {listing?.bucket ?? '…'}
          </Link>
          {crumbs.map((p, i) => (
            <Link
              key={i}
              component='button'
              underline='hover'
              onClick={() => open(crumbs.slice(0, i + 1).join('/') + '/')}
              sx={{ color: i === crumbs.length - 1 ? c.ink : c.muted }}
            >
              {p}
            </Link>
          ))}
        </Breadcrumbs>
      }
      action={
        root && listing && listing.prefix !== root.prefix ? (
          <Button size='small' onClick={() => open(root.prefix)}>
            Back to linked folder
          </Button>
        ) : undefined
      }
    >
      {err && (
        <Typography sx={{ p: 2.5, color: 'error.main' }}>{err}</Typography>
      )}
      {!listing && !err && (
        <Typography sx={{ p: 2.5, color: c.muted }}>Loading files…</Typography>
      )}
      {listing && images.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            p: 2,
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            borderBottom: `1px solid ${c.line}`,
          }}
        >
          {images.slice(0, 24).map((e) => (
            <Box
              key={e.key}
              onClick={() => show(e)}
              sx={{
                cursor: 'pointer',
                borderRadius: '10px',
                overflow: 'hidden',
                border: `1px solid ${c.line}`,
                bgcolor: c.bg,
              }}
              title={e.name}
            >
              <Thumb src={fileURL(listing.bucket, e.key)} height={110} />
              <Typography
                sx={{ fontSize: 11, px: 1, py: 0.5, color: c.muted }}
                noWrap
              >
                {e.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {listing?.entries.length === 0 && (
        <Typography sx={{ p: 2.5, color: c.muted }}>
          This folder is empty.
        </Typography>
      )}
      {listing?.entries.map((e) => (
        <Box
          key={e.key}
          onClick={() => (e.folder ? open(e.key) : show(e))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1,
            cursor: 'pointer',
            '& + &': { borderTop: `1px solid ${c.line}` },
            '&:hover': { bgcolor: c.hover },
          }}
        >
          {e.folder ? (
            <Folder sx={{ color: c.muted }} />
          ) : isImage(e.name) ? (
            <ImageIcon sx={{ color: c.muted }} />
          ) : (
            <InsertDriveFile sx={{ color: c.muted }} />
          )}
          <Typography sx={{ flex: 1, fontSize: 14 }} noWrap>
            {e.name}
          </Typography>
          {!e.folder && (
            <Typography sx={{ fontSize: 12, color: c.muted }}>
              {size(e.size)}
              {e.modified && ` · ${new Date(e.modified).toLocaleString()}`}
            </Typography>
          )}
          {e.folder && <ChevronRight sx={{ color: c.muted }} />}
        </Box>
      ))}
      {listing?.more && (
        <Typography sx={{ p: 2, fontSize: 12, color: c.muted }}>
          Showing the first 500 items.
        </Typography>
      )}
      {listing && (
        <Preview
          bucket={listing.bucket}
          files={files}
          index={preview}
          onIndex={setPreview}
        />
      )}
    </Panel>
  )
}
